import React, {useEffect, useRef, useState} from "react";
import Chart from "chart.js";
import {getReport} from "../../../services/Trading";
import EmptyPeriodState from "../../EmptyPeriodState";
import {useTranslation} from "react-i18next";
import {
    DoughnutWrap,
    DoughnutCanvasWrap,
    DoughnutFooter,
    DoughnutFooterBlock,
    DoughnutFooterTop,
    DoughnutFooterMiniTrack,
    DoughnutFooterMiniFill,
} from "./styles";

export default function GeneralDoughnut({filter, ...props}) {
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const { t } = useTranslation();

    const [qtdeWin, setQtdeWin] = useState(0);
    const [qtdeLoss, setQtdeLoss] = useState(0);
    const [qtdeDraw, setQtdeDraw] = useState(0);
    const [isData, setData] = useState(null);
    const [percentDrawState, setPercentDrawState] = useState(0);
    const [percentLossState, setPercentLossState] = useState(0);
    const [percentWinState, setPercentWinState] = useState(0);

    useEffect(() => {
        const total = Number(qtdeLoss) + Number(qtdeWin) + Number(qtdeDraw);
        if (!total) {
            setPercentDrawState(0);
            setPercentLossState(0);
            setPercentWinState(0);
            return;
        }
        setPercentDrawState(Number(((qtdeDraw * 100) / total).toFixed(2)));
        setPercentLossState(Number(((qtdeLoss * 100) / total).toFixed(2)));
        setPercentWinState(Number(((qtdeWin * 100) / total).toFixed(2)));
    }, [qtdeWin, qtdeLoss, qtdeDraw]);

    useEffect(() => {
        if (!isData) {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
                chartInstanceRef.current = null;
            }
            return;
        }

        if (!chartRef.current) {
            return;
        }

        const total = Number(qtdeLoss) + Number(qtdeWin) + Number(qtdeDraw);
        if (!total) {
            return;
        }

        const percentLoss = ((qtdeLoss * 100) / total).toFixed(2);
        const percentDraw = ((qtdeDraw * 100) / total).toFixed(2);
        const percentWin = ((qtdeWin * 100) / total).toFixed(2);

        const ctx = chartRef.current.getContext("2d");
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy();
            chartInstanceRef.current = null;
        }

        chartInstanceRef.current = new Chart(ctx, {
            type: "doughnut",
            data: {
                labels: [
                    `${qtdeLoss} ${t('defeats')}`,
                    `${qtdeDraw} ${t('draws')}`,
                    `${qtdeWin} ${t('victories')}`,
                ],
                datasets: [{
                    data: [percentLoss, percentDraw, percentWin].map((v) => {
                        const n = parseFloat(String(v));
                        return Number.isFinite(n) ? n : 0;
                    }),
                    backgroundColor: [
                        "rgba(231, 74, 59, 0.92)",
                        "rgba(140, 148, 168, 0.88)",
                        "rgba(28, 200, 138, 0.92)",
                    ],
                    borderColor: "rgba(18, 22, 42, 0.98)",
                    borderWidth: 2,
                    hoverBorderColor: "rgba(242, 146, 5, 0.5)",
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutoutPercentage: 62,
                legend: {
                    display: true,
                    position: "right",
                    labels: {
                        fontColor: "rgba(220, 228, 245, 0.92)",
                        fontSize: 11,
                        boxWidth: 12,
                        padding: 14,
                        usePointStyle: true,
                    },
                },
                tooltips: {
                    enabled: true,
                    backgroundColor: "rgba(18, 22, 42, 0.96)",
                    titleFontColor: "rgba(245, 248, 255, 0.98)",
                    bodyFontColor: "rgba(230, 235, 250, 0.92)",
                    borderColor: "rgba(242, 146, 5, 0.35)",
                    borderWidth: 1,
                    xPadding: 10,
                    yPadding: 10,
                    cornerRadius: 8,
                },
                layout: {
                    padding: 4,
                },
            },
        });

        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
                chartInstanceRef.current = null;
            }
        };
    }, [qtdeWin, qtdeLoss, qtdeDraw, isData, t]);

    useEffect(() => {
        getReport({
            startDate: filter[0].startDate,
            endDate: filter[0].endDate,
            direction: filter[0].direction,
            orderBy: 'profitObtained',
            order: 'DESC'
        }).then((data) => {
            if (!data || data === '' || !data.data) {
                setData(false);
                return;
            }

            setData((data.data.win.qtde + data.data.loss.qtde + data.data.draw.qtde) > 0);
            setQtdeWin(data.data.win.qtde);
            setQtdeLoss(data.data.loss.qtde);
            setQtdeDraw(data.data.draw.qtde);
        });
    }, [filter]);

    return (
        <div {...props}>
            {isData ?
                <>
                    <DoughnutWrap>
                        <DoughnutCanvasWrap>
                            <canvas id="generalDoughnutChart" ref={chartRef} aria-hidden/>
                        </DoughnutCanvasWrap>
                    </DoughnutWrap>
                    <DoughnutFooter>
                        <DoughnutFooterBlock>
                            <DoughnutFooterTop>
                                <i className="fas fa-circle" style={{color: '#e74a3b'}}/>
                                <span className="pct-value">{parseFloat(percentLossState).toFixed(2)}%</span>
                            </DoughnutFooterTop>
                            <DoughnutFooterMiniTrack>
                                <DoughnutFooterMiniFill
                                    style={{
                                        width: `${Math.min(100, Math.max(0, percentLossState))}%`,
                                        background: 'linear-gradient(90deg, #c0392b 0%, #e74a3b 100%)',
                                    }}
                                />
                            </DoughnutFooterMiniTrack>
                        </DoughnutFooterBlock>
                        <DoughnutFooterBlock>
                            <DoughnutFooterTop>
                                <i className="fas fa-circle" style={{color: '#8c94a8'}}/>
                                <span className="pct-value">{parseFloat(percentDrawState).toFixed(2)}%</span>
                            </DoughnutFooterTop>
                            <DoughnutFooterMiniTrack>
                                <DoughnutFooterMiniFill
                                    style={{
                                        width: `${Math.min(100, Math.max(0, percentDrawState))}%`,
                                        background: 'linear-gradient(90deg, #6b7080 0%, #9aa0b0 100%)',
                                    }}
                                />
                            </DoughnutFooterMiniTrack>
                        </DoughnutFooterBlock>
                        <DoughnutFooterBlock>
                            <DoughnutFooterTop>
                                <i className="fas fa-circle" style={{color: '#1cc88a'}}/>
                                <span className="pct-value">{parseFloat(percentWinState).toFixed(2)}%</span>
                            </DoughnutFooterTop>
                            <DoughnutFooterMiniTrack>
                                <DoughnutFooterMiniFill
                                    style={{
                                        width: `${Math.min(100, Math.max(0, percentWinState))}%`,
                                        background: 'linear-gradient(90deg, #13855c 0%, #1cc88a 100%)',
                                    }}
                                />
                            </DoughnutFooterMiniTrack>
                        </DoughnutFooterBlock>
                    </DoughnutFooter>
                </> :
                <EmptyPeriodState iconClassName="fas fa-chart-pie"/>
            }
        </div>
    );
}
